// mealPlanService.js - Services for meal plan generation
import {
  MODEL_CONFIG,
  MODEL_REASONING_CONFIG,
  getProviderForModel,
  cleanJsonResponse,
  generationCostTracker,
  supportsOpenAIReasoning,
} from './apiConfig';
import { streamFromBackend } from './aiProxyClient';
import { generateMealPlanPrompt } from '../utils/promptGenerators';
import { getUserCountry } from './apiService';
import { storePromptForTestFlow, clearStoredPrompt } from '../utils/testFlowLogger';

const EXPECTED_MEAL_PLAN_RECIPES = 9;

const extractFirstBalancedJsonObject = (text) => {
  if (!text) return null;

  const start = text.indexOf('{');
  if (start === -1) return null;

  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < text.length; i++) {
    const char = text[i];

    if (escaped) {
      escaped = false;
      continue;
    }

    if (char === '\\') {
      escaped = inString;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      continue;
    }

    if (inString) continue;

    if (char === '{') depth++;
    if (char === '}') depth--;

    if (depth === 0) {
      return text.slice(start, i + 1);
    }
  }

  return null;
};

const normalizeMealPlanPayload = (payload) => {
  if (!payload || !Array.isArray(payload.mealPlan)) return payload;

  return {
    ...payload,
    mealPlan: payload.mealPlan.map(item => {
      if (item && item.ingredients && !Array.isArray(item.ingredients) && typeof item.ingredients === 'object') {
        return { ...item, ingredients: Object.entries(item.ingredients).map(([product, qty]) => ({ product, qty })) };
      }
      return item;
    }),
  };
};

const getRecipeCount = (payload) => Array.isArray(payload?.mealPlan) ? payload.mealPlan.length : 0;

const attachMealPlanParseMetadata = (payload, metadata) => ({
  ...payload,
  _mealPlanParse: {
    ...metadata,
    finalRecipeCount: getRecipeCount(payload),
    isPartial: getRecipeCount(payload) > 0 && getRecipeCount(payload) < EXPECTED_MEAL_PLAN_RECIPES,
  },
});

/**
 * Function to extract and process products array from streaming response
 * @param {string} text - The text to extract products from
 * @returns {Array|null} Extracted products or null if none found
 */
const extractProducts = (text) => {
  try {
    // Look for products array in the text
    const productsMatch = /"products"\s*:\s*(\[[\s\S]*?])/gs.exec(text);
    
    if (!productsMatch || !productsMatch[1]) {
      return null;
    }
    
    // Extract just the products array content
    let productsText = productsMatch[1];
    
    // Try to identify complete category objects first
    const categoryPattern = /\{\s*"category"\s*:\s*"([^"]+)"\s*,\s*"items"\s*:\s*\[([\s\S]*?)\]\s*\}/g;
    const categories = [];
    let categoryMatch;
    
    while ((categoryMatch = categoryPattern.exec(productsText)) !== null) {
      try {
        const categoryName = categoryMatch[1];
        const itemsText = categoryMatch[2];
        
        // Extract individual items from this category
        const items = [];
        const itemPattern = /\{([\s\S]*?)\}/g;
        let itemMatch;
        
        while ((itemMatch = itemPattern.exec(itemsText)) !== null) {
          try {
            // Prepare a valid JSON object string
            const itemText = `{${itemMatch[1]}}`;
            
            // Fix trailing commas which are invalid in JSON
            const fixedItemText = itemText
              .replace(/,\s*}/g, '}')
              .replace(/,\s*]/g, ']');
            
            // Parse the item
            const item = JSON.parse(fixedItemText);
            items.push(item);
          } catch (e) {
            console.warn('Error parsing individual product item:', e);
          }
        }
        
        // Add the category with its items
        let finalItems = items;
        if (
          items.length === 1 &&
          items[0].category === categoryName &&
          Array.isArray(items[0].items)
        ) {
          finalItems = items[0].items;
        }
        if (finalItems.length > 0) {
          categories.push({
            category: categoryName,
            items: finalItems
          });
        }
      } catch (e) {
        console.warn('Error processing category:', e);
      }
    }
    
    // If we found complete categories, return them
    if (categories.length > 0) {
      return categories;
    }
    
    // Fallback: Try to extract individual products without categories
    const itemsArray = [];
    const itemPattern = /\{([\s\S]*?)\}/g;
    let itemMatch;
    
    while ((itemMatch = itemPattern.exec(productsText)) !== null) {
      try {
        // Prepare a valid JSON object string
        const itemText = `{${itemMatch[1]}}`;
        
        // Fix trailing commas which are invalid in JSON
        const fixedItemText = itemText
          .replace(/,\s*}/g, '}')
          .replace(/,\s*]/g, ']');
        
        // Parse the item
        if (fixedItemText.includes('"productName"')) {
          const item = JSON.parse(fixedItemText);
          itemsArray.push(item);
        }
      } catch (e) {
        console.warn('Error parsing product item:', e);
      }
    }
    
    // Group individual items by category
    if (itemsArray.length > 0) {
      const groupedByCategory = {};
      
      for (const item of itemsArray) {
        const category = item.category || "Other";
        if (!groupedByCategory[category]) {
          groupedByCategory[category] = [];
        }
        groupedByCategory[category].push(item);
      }
      
      // Convert to array of category objects
      return Object.entries(groupedByCategory).map(([category, items]) => ({
        category,
        items
      }));
    }
    
    return null;
  } catch (e) {
    console.error('Error extracting products:', e);
    return null;
  }
};

/**
 * Fetch meal plan with streaming support
 * 
 * @param {Object} params - Parameters for meal plan generation
 * @param {Function} params.onRecipeComplete - Called immediately when a complete recipe is parsed (for progressive processing)
 * @returns {Promise<Object>} Generated meal plan
 */
export const fetchMealPlan = async ({
  userCountry,
  userBudget,
  numberOfPeople,
  selectedCuisine,
  dietaryPreferences,
  cookingStyle,
  onPartialResponse,
  onRecipe,
  onRecipeComplete, // NEW: Callback for immediate recipe processing
  onReasoning
}) => {
  // Use provided country or get from global setting
  const effectiveCountry = userCountry || getUserCountry();

  // NOTE: cost tracker is reset/printed by useMealPlanLoader at the loadPlan boundary,
  // so structure-retry attempts accumulate into a single summary.

  // Clear any previous stored prompt
  clearStoredPrompt();
  
  const prompt = await generateMealPlanPrompt({
    userCountry: effectiveCountry,
    userBudget,
    numberOfPeople,
    selectedCuisine,
    dietaryPreferences,
    cookingStyle,
  });

  // Store prompt for TESTFLOW logging
  storePromptForTestFlow(prompt);

  let fullResponse = '';
  let finalTextResponse = '';
  let finalTextReceived = false;
  let partialData = {
    mealPlan: [],
    products: [],
    totalCost: ''
  };
  
  // Track which recipes have been sent via onRecipeComplete to avoid duplicates
  const completedRecipeTitles = new Set();
  
  // Patterns to extract data from streaming responses
  const recipePattern = /"title":\s*"([^"]+)"/g;
  const costPattern = /"totalCost":\s*"([^"]+)"/;            // non-global to avoid lastIndex issues
  
  // Function to extract and process complete recipe objects
  const extractRecipes = (text) => {
    const recipes = [];
    let recipeMatch;
    
    // Reset the regex to start from the beginning
    recipePattern.lastIndex = 0;
    
    // Look for recipe title markers
    while ((recipeMatch = recipePattern.exec(text)) !== null) {
      try {
        const titlePos = recipeMatch.index;
        
        // Find the opening brace for this recipe object
        let startPos = titlePos;
        while (startPos > 0 && text[startPos] !== '{') startPos--;
        if (text[startPos] !== '{') continue;
        
        // Find the matching closing brace using brace counting
        let braceCount = 1;
        let endPos = startPos + 1;
        
        while (braceCount > 0 && endPos < text.length) {
          if (text[endPos] === '{') braceCount++;
          if (text[endPos] === '}') braceCount--;
          endPos++;
        }
        
        // If we couldn't find a matching closing brace, skip this recipe
        if (braceCount !== 0) continue;
        
        // Extract the recipe JSON (endPos - 1 because we incremented past the closing brace)
        const recipeText = text.substring(startPos, endPos);
        
        // Check if it has the required fields before trying to parse
        if (recipeText.includes('"title"') && 
            recipeText.includes('"mealType"') && 
            recipeText.includes('"ingredients"')) {
          
          try {
            const recipe = JSON.parse(recipeText);
            
            // Normalize ingredients to array if provided as an object map
            if (recipe && recipe.ingredients && !Array.isArray(recipe.ingredients) && typeof recipe.ingredients === 'object') {
              recipe.ingredients = Object.entries(recipe.ingredients).map(([product, qty]) => ({ product, qty }));
            }
            
            // Validate recipe has all required fields
            if (recipe.title && recipe.mealType && Array.isArray(recipe.ingredients)) {
              // Check if we already have this recipe
              if (!partialData.mealPlan.some(r => r.title === recipe.title)) {
                recipes.push(recipe);
                
                // Call the onRecipe callback if provided
                if (onRecipe) onRecipe(recipe);
                
                // PROGRESSIVE: Immediately trigger recipe details processing
                // This allows parallel processing - as soon as a recipe is parsed,
                // we can start fetching its details and generating images
                if (onRecipeComplete && !completedRecipeTitles.has(recipe.title)) {
                  completedRecipeTitles.add(recipe.title);
                  console.log(`[MealPlanService] 🚀 Recipe "${recipe.title}" complete - triggering immediate processing`);
                  // Fire and forget - don't block the stream
                  Promise.resolve().then(() => onRecipeComplete(recipe));
                }
              }
            }
          } catch (parseError) {
            // Skip this recipe if it's not valid JSON
            console.warn('Failed to parse recipe:', parseError);
          }
        }
      } catch (e) {
        console.warn('Error processing recipe match:', e);
      }
    }

    return recipes;
  };

  const completionModel = MODEL_CONFIG.completion;
  const completionProvider = getProviderForModel(completionModel);
  let streamAttempt = 0;

  try {
    console.log(`Using ${completionProvider} (${completionModel}) for meal plan generation for country: ${effectiveCountry}`);
    console.log("Prompt:", prompt);
    
    const systemPrompt = "You are smart meal planning assistant that responds only in valid JSON format as specified in the user prompt.";

    // Build request body for the backend AI proxy
    const requestBody = {
      model: completionModel,
      systemPrompt,
      userPrompt: prompt,
      maxTokens: 5500,
    };

    // Add provider-specific params
    // Extended thinking for Claude Haiku 4.5 (requires backend AIProxyService to forward `thinking` param).
    // When enabled: temperature MUST be 1, assistantPrefill is NOT allowed, maxTokens MUST exceed budget_tokens.
    const ANTHROPIC_THINKING_ENABLED = true;
    const ANTHROPIC_THINKING_BUDGET = 2000;

    if (completionProvider === 'openai' && supportsOpenAIReasoning(completionModel)) {
      const reasoningConfig = MODEL_REASONING_CONFIG.completion || {};
      requestBody.reasoning = { effort: reasoningConfig.effort || 'medium' };
      if (reasoningConfig.maxTokens) {
        requestBody.maxTokens = Math.max(requestBody.maxTokens, reasoningConfig.maxTokens);
      }
    } else if (completionProvider === 'anthropic') {
      if (ANTHROPIC_THINKING_ENABLED) {
        requestBody.thinking = { type: 'enabled', budget_tokens: ANTHROPIC_THINKING_BUDGET };
        requestBody.temperature = 1;
      } else {
        requestBody.temperature = 0.7;
        requestBody.assistantPrefill = '{"reasoning": "';
      }
    }

    // Retry once if we get an empty response
    const MAX_STREAM_ATTEMPTS = 2;

    const consumeStreamAttempt = async (needsPrefillInitial) => {
      let needsPrefill = needsPrefillInitial;

      await streamFromBackend('/completion', requestBody, {
        onText: (textChunk) => {
          if (!textChunk) return;

          // On first Anthropic chunk, prepend the prefill that isn't echoed back
          if (needsPrefill && fullResponse.length === 0) {
            fullResponse = '{"reasoning": "' + textChunk;
            needsPrefill = false;
          } else {
            fullResponse += textChunk;
          }

          // Extract and process recipes
          const newRecipes = extractRecipes(fullResponse);
          if (newRecipes.length > 0) {
            partialData.mealPlan = [...partialData.mealPlan, ...newRecipes];
            if (onPartialResponse) onPartialResponse({ ...partialData });
          }

          // Extract products as they come in
          if (textChunk.includes('"products"') || textChunk.includes('"category"') || textChunk.includes('"productName"')) {
            const extractedProducts = extractProducts(fullResponse);
            if (extractedProducts && extractedProducts.length > 0) {
              partialData.products = extractedProducts;
              if (onPartialResponse) onPartialResponse({ ...partialData });
            }
          }

          // Update other fields if found
          const costMatch = costPattern.exec(textChunk);
          if (costMatch && costMatch[1]) {
            partialData.totalCost = costMatch[1];
            if (onPartialResponse) onPartialResponse({ ...partialData });
          }
        },
        onReasoning: (text) => {
          if (text && onReasoning) onReasoning(text);
        },
        onFinalText: (text) => {
          if (!text) return;
          finalTextReceived = true;
          finalTextResponse = text;
        },
        onUsage: (usage) => {
          if (usage?.input_tokens && usage?.output_tokens) {
            const cached = usage.input_tokens_details?.cached_tokens || usage.cache_read_input_tokens || 0;
            generationCostTracker.logCompletion(completionModel, usage.input_tokens, usage.output_tokens, cached);
          }
        },
        onError: (err) => {
          console.error('Stream error:', err);
        },
      });
    };

    while (streamAttempt < MAX_STREAM_ATTEMPTS) {
      streamAttempt++;
      await consumeStreamAttempt(completionProvider === 'anthropic' && !requestBody.thinking);

      console.log(`Stream ended (attempt ${streamAttempt}/${MAX_STREAM_ATTEMPTS}). Total response length: ${fullResponse.length} chars, finalText=${finalTextReceived ? finalTextResponse.length : 0} chars`);

      // If we got content, break
      if (fullResponse.length > 0 || finalTextResponse.length > 0 || partialData.mealPlan.length > 0) {
        break;
      }

      // Empty response — retry once
      if (streamAttempt < MAX_STREAM_ATTEMPTS) {
        console.warn(`⚠️ Empty response (attempt ${streamAttempt}), retrying...`);
        fullResponse = '';
        finalTextResponse = '';
        finalTextReceived = false;
        partialData = { mealPlan: [], products: [], totalCost: '' };
        completedRecipeTitles.clear();
        continue;
      }

      console.error(`❌ Empty response after ${MAX_STREAM_ATTEMPTS} attempts`);
    } // end while

    // Prefer the backend/provider authoritative finalText when available.
    const authoritativeResponse = finalTextReceived && finalTextResponse.trim()
      ? finalTextResponse
      : fullResponse;
    const authoritativeSource = finalTextReceived && finalTextResponse.trim() ? 'finalText' : 'stream';
    const cleanedResponse = cleanJsonResponse(authoritativeResponse);
    const baseParseMetadata = {
      source: authoritativeSource,
      model: completionModel,
      provider: completionProvider,
      streamAttempt,
      finalTextReceived,
      fullResponseLength: fullResponse.length,
      finalTextLength: finalTextResponse.length,
      authoritativeResponseLength: authoritativeResponse.length,
      cleanedResponseLength: cleanedResponse.length,
      partialRecipeCount: partialData.mealPlan.length,
    };

    console.log("Full response before cleaning:", authoritativeResponse);
    console.log("Cleaned response:", cleanedResponse);
    
    // Try parsing the complete response
    try {
      if (authoritativeResponse && authoritativeResponse.trim()) {
        let jsonData;
        let parseStatus = 'final-json';
        const directCandidate = authoritativeResponse.trim();

        try {
          jsonData = JSON.parse(directCandidate);
        } catch (directParseError) {
          parseStatus = 'final-json-after-clean';
          jsonData = JSON.parse(cleanedResponse);
        }

        jsonData = normalizeMealPlanPayload(jsonData);
        const result = attachMealPlanParseMetadata(jsonData, {
          ...baseParseMetadata,
          parseStatus,
        });
        
        // One final update with the complete data
        if (onPartialResponse) onPartialResponse(result);
        
        // Return the complete JSON object
        return result;
      } else {
        console.log("Empty or invalid JSON response, using partial data");
        throw new Error("Empty JSON response");
      }
    } catch (parseError) {
      console.warn("JSON parsing error:", parseError.message);
      console.warn("Failed to parse response:", cleanedResponse);

      const balancedCandidate = extractFirstBalancedJsonObject(authoritativeResponse) || extractFirstBalancedJsonObject(cleanedResponse);
      if (balancedCandidate) {
        try {
          const balancedData = normalizeMealPlanPayload(JSON.parse(balancedCandidate));
          const result = attachMealPlanParseMetadata(balancedData, {
            ...baseParseMetadata,
            parseStatus: 'final-json-after-balanced-extraction',
            parseError: parseError.message,
            balancedResponseLength: balancedCandidate.length,
          });

          console.warn("Recovered meal plan via balanced JSON extraction", result._mealPlanParse);
          if (onPartialResponse) onPartialResponse(result);
          return result;
        } catch (balancedError) {
          console.warn("Balanced JSON recovery failed:", balancedError.message);
        }
      }
      
      // Try to fix incomplete JSON by finding where it breaks
      if (cleanedResponse && cleanedResponse.trim()) {
        console.log("Attempting to recover partial JSON data...");
        try {
          // Find the last complete recipe in the mealPlan array
          const mealPlanMatch = /"mealPlan"\s*:\s*\[([\s\S]*)/i.exec(cleanedResponse);
          if (mealPlanMatch) {
            // Extract what we can from partialData which has already parsed recipes
            if (partialData.mealPlan.length > 0) {
              console.log(`Recovered ${partialData.mealPlan.length} recipes from partial data`);
              
              // Try to extract totalCost if it exists
              const costMatch = /"totalCost"\s*:\s*"([^"]+)"/i.exec(cleanedResponse);
              if (costMatch) {
                partialData.totalCost = costMatch[1];
              }
              
              // Try to extract Number of people if it exists
              const peopleMatch = /"Number of people"\s*:\s*(\d+)/i.exec(cleanedResponse);
              if (peopleMatch) {
                partialData["Number of people"] = parseInt(peopleMatch[1]);
              }
              
              const result = attachMealPlanParseMetadata(partialData, {
                ...baseParseMetadata,
                parseStatus: 'partial-recovery',
                parseError: parseError.message,
              });
              console.log("Returning recovered partial data:", result);
              if (onPartialResponse) onPartialResponse(result);
              return result;
            }
          }
        } catch (recoveryError) {
          console.warn("Failed to recover partial JSON:", recoveryError.message);
        }
      }
      
      // If we have partial data but final parse failed, return what we have
      if (partialData.mealPlan.length > 0) {
        console.log("Returning partial data despite final parse error");
        return attachMealPlanParseMetadata(partialData, {
          ...baseParseMetadata,
          parseStatus: 'partial-after-error',
          parseError: parseError.message,
        });
      }
      
      throw new Error(`Failed to parse response: ${parseError.message}`);
    }

  } catch (error) {
    console.error(`Error fetching meal plan (${completionProvider}):`, error.message);
    console.error("Full response:", fullResponse);
    
    // If we have partial data but hit an error, return what we have
    if (partialData.mealPlan.length > 0) {
      console.log("Returning partial data despite error");
      return attachMealPlanParseMetadata(partialData, {
        parseStatus: 'partial-after-error',
        source: finalTextReceived && finalTextResponse.trim() ? 'finalText' : 'stream',
        model: completionModel,
        provider: completionProvider,
        streamAttempt,
        finalTextReceived,
        fullResponseLength: fullResponse.length,
        finalTextLength: finalTextResponse.length,
        authoritativeResponseLength: (finalTextReceived && finalTextResponse.trim() ? finalTextResponse : fullResponse).length,
        cleanedResponseLength: cleanJsonResponse(finalTextReceived && finalTextResponse.trim() ? finalTextResponse : fullResponse).length,
        partialRecipeCount: partialData.mealPlan.length,
        parseError: error.message,
      });
    }
    
    throw error;
  }
};